/**
 * This component is currently unused, leaving it here in case we need a dynamic Tabs system rather the static
 * TabsNavigation currently in the Comparison page
 */

// Third-party
import React from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

// Components
import { DropdownMenu } from '../inputs';
import { RoundedPastille } from '../misc';
import { Tooltip } from '../tooltips';

export type TabItem = {
  name: string;
  value: any;
  isDirty?: boolean;
};

type Props = {
  className?: string;
  items: TabItem[];
  selected: any;
  onChange: (value: any) => void;
  onClose: (value: any) => void;
  onNew?: () => void;
  onSelectFromMore?: (value: any) => void;
};

type State = {
  maxDisplayedTabs: number;
};

class DropdownTabs extends React.Component<Props, State> {
  private containerRef = React.createRef<HTMLDivElement>();

  private TAB_WIDTH = 200;

  readonly state: State = {
    maxDisplayedTabs: 10,
  };

  static defaultProps = {
    onChange: () => {},
    onClose: () => {},
  };

  componentDidMount() {
    this.updateTabsSizing();
    window.addEventListener('resize', this.updateTabsSizing);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateTabsSizing);
  }

  updateTabsSizing = debounce(
    () => {
      if (this.containerRef.current) {
        const totalWidth = this.containerRef.current.offsetWidth;

        /**
         * Substracting 150px to the total width to take into account the "+" tab appended at the end,
         * and the "..." menu at the start.
         */
        this.setState({
          maxDisplayedTabs: Math.floor((totalWidth - 150) / this.TAB_WIDTH),
        });
      }
    },
    200,
    { leading: true },
  );

  onTabClose = (value: any, event: React.MouseEvent) => {
    const { onClose } = this.props;
    event.stopPropagation();
    return onClose(value);
  };

  getDropdownOptions = () => {
    const { items, onSelectFromMore, onChange } = this.props;
    const { maxDisplayedTabs } = this.state;

    return items.slice(0, items.length - maxDisplayedTabs).map((item, i) => (
      <button key={i} onClick={() => (onSelectFromMore ? onSelectFromMore(item.value) : onChange(item.value))}>
        {item.isDirty && <div className="dirty-icon" />}
        <span>{item.name}</span>
      </button>
    ));
  };

  render() {
    const { className, items, selected, onChange, onNew } = this.props;
    const { maxDisplayedTabs } = this.state;

    return (
      <div className={classNames('dropdown-tabs', className)}>
        <div ref={this.containerRef} className="tabs-container d-flex">
          {items.length > maxDisplayedTabs && (
            <DropdownMenu className="d-flex align-items-center" options={this.getDropdownOptions()}>
              <div className="tab more-tab d-flex align-items-center">
                <span>...</span>
                <RoundedPastille>{items.length - maxDisplayedTabs}</RoundedPastille>
              </div>
            </DropdownMenu>
          )}

          {items.slice(-maxDisplayedTabs).map((item, i) => {
            const isSelected = item.value === selected;
            return (
              <div
                key={i}
                className={classNames('tab d-flex', { selected: isSelected })}
                onClick={() => onChange(item.value)}
                style={{ width: this.TAB_WIDTH }}
              >
                <div className="tab-content flex-fill d-flex align-items-center">
                  <Tooltip delayShow={300} place="bottom" message={item.name} data-private data-dd-privacy="mask">
                    <div className="tab-label">
                      <span>{item.name}</span>
                    </div>
                  </Tooltip>

                  <div className="tab-icon">
                    {item.isDirty && <div className="dirty-icon" />}

                    <div className="close-tab" onClick={this.onTabClose.bind(null, item.value)}>
                      <i className="icon-cross" />
                    </div>
                  </div>
                </div>

                <div className="rounded-border" />
              </div>
            );
          })}

          {onNew && (
            <div className={classNames('tab new-tab d-flex')} onClick={onNew}>
              <div className="tab-content flex-fill d-flex align-items-center">
                <div className="tab-label">
                  <i className="icon-plus" />
                </div>
              </div>

              <div className="rounded-border" />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default DropdownTabs;
